import WorkunitApiService from 'api/WorkunitApiService';
import useBLworkunitsService from './useBaseLineWorkunitsService';
import useApi from 'hooks/useApi';

import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import { injectWorkunitsAction, injectSelectedWorkunitsAction } from 'store/actions/catalogToOrderActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import ChangeBaseLineAPIServices from 'api/ChangeBaseLineAPIServices';
import Order from 'types/entities/Order';
import { generateRandomId } from 'features/Orders/OrderWorkunits/helpersOrderWorkunits';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import { filterTotalFlagAction } from 'store/actions/changeBaselineActions';

const useBaseLineOrderWorkunitApi = () => {
  const { makeCall } = useApi();
  const { dispatch } = useAppState();
  const history = useHistory();
  const order = useSelector((state) => state.catalogToOrder.order);
  const { createBodyPostBulkOrderWorkunits, generateMonthsBSQuarters, formateRows } = useBLworkunitsService();

  const bulkCreateBaseLineOrderWorkunits = async (
    source_id: Order['id'],
    baselineOrder_id: Order['id'],
    baseline_version: number
  ) => {
    if (order) {
      const orderWorkunits = createBodyPostBulkOrderWorkunits(baseline_version);
      const res = await makeCall(
        ChangeBaseLineAPIServices.postBulkBLOrderWorkunits({
          order_baseline_id: baselineOrder_id,
          source_order_id: source_id,
          page_typeid: 2,
          order_workunits: orderWorkunits,
          baseline_version,
        })
      );
      return res;
    }

    return null; // or `undefined`
  };

  const fetchBaselineWorkunitsInCatalog = React.useCallback(
    async (catalogId: number) => {
      const timeValues = generateMonthsBSQuarters();
      if (timeValues) {
        dispatch(addLoadingAction('fetchBaselineWorkunitsInCatalog'));
        const res = await makeCall(
          WorkunitApiService.get(catalogId, {
            join: ['complexity'],
            size: -1,
          })
        );
        const rowsToDisplay = formateRows(res.datas, timeValues);
        dispatch(injectWorkunitsAction(rowsToDisplay));
        dispatch(removeLoadingAction('fetchBaselineWorkunitsInCatalog'));
      }
    },
    [dispatch, formateRows, generateMonthsBSQuarters, makeCall]
  );

  const getBLOrderNewWorkunits = useCallback(
    async (baselineOrder_id: number, page_typeid: number, order_source_id: number, baseline_version: number) => {
      dispatch(addLoadingAction('getOrderWorkunits'));
      const data = await makeCall(
        ChangeBaseLineAPIServices.getOrderNewWorkunitInOrder(
          Number(baselineOrder_id),
          page_typeid,
          Number(order_source_id),
          baseline_version
        )
      );
      if (data.order_workunits && data.order_workunits.length > 0) {
        const datas = [];
        for (let i = 0; i < data.order_workunits.length; i += 1) {
          datas.push({
            ...data.order_workunits[i],
            randomId: Number(generateRandomId() + i),
          });
        }
        dispatch(
          injectSelectedWorkunitsAction(
            datas.map((owu: any, i: number) => ({
              ...owu,
              name: owu.workunit.name,
              reference: owu.workunit.reference,
              workload: owu.charge,
              scope_id: owu.scope.id,
              total: owu.months.reduce((acc: number, month: any) => acc + month.qte, 0),
              months: owu.months.map((month: any) => ({
                ...month,
                quantity: month.qte,
              })),
            }))
          )
        );
      }
    },
    [dispatch, makeCall]
  );

  const fetchBaselineOrderSummaryPage = async (baselineOrder_id: Order['id']) => {
    await makeCall(ChangeBaseLineAPIServices.fetchBaselineOrderSummary(Number(baselineOrder_id)));
  };

  const EditTotalBaseLineWorkunit = React.useCallback(
    async (baselineOrder_id: Order['id'], savedDeliverables: OrderWorkunit, source_id: Order['id']) => {
      dispatch(addLoadingAction('editTotalBaseLineWorkunit'));
      const res = await ChangeBaseLineAPIServices.updateBaseLineWorkunit(Number(baselineOrder_id), {
        ...savedDeliverables,
        order_id: Number(source_id),
      });
      dispatch(filterTotalFlagAction(true));
      dispatch(removeLoadingAction('editTotalBaseLineWorkunit'));
      return res;
    },
    [dispatch, makeCall]
  );

  const submitBaseLineOrder = async (
    source_id: Order['id'],
    baselineOrder_id: Order['id'],
    baseline_version: number,
    page_typeid: number
  ) => {
    const res = await makeCall(
      ChangeBaseLineAPIServices.postSubmitBaseline({
        order_baseline_id: baselineOrder_id,
        source_order_id: source_id,
        page_typeid,
        baseline_version,
      })
    );
    return res;
  };

  return {
    bulkCreateBaseLineOrderWorkunits,
    fetchBaselineWorkunitsInCatalog,
    getBLOrderNewWorkunits,
    fetchBaselineOrderSummaryPage,
    EditTotalBaseLineWorkunit,
    submitBaseLineOrder,
  };
};

export default useBaseLineOrderWorkunitApi;
